import { Component, h, Prop, State, Watch } from '@stencil/core';
import { translations, markets } from './pn-find-service-and-price-translations';
import { ProductPriceSourceData, ProductPriceItem } from '../../../globals/shared/productprice/product-price-types';
import {
  FindServiceAndPricePostageType,
  FindServiceAndPriceWeight,
  FindServiceAndPriceDeliveryScope,
  PnfindServiceAndPriceMarket,
  PnfindServiceAndPricei18n,
} from './pn-find-service-and-price-types';
import { postagetypes, weights, deliveryscopes } from './data.js';
import { convertFileFromStream } from '../../../globals/shared/productprice/convert.js';
import { state } from './pn-find-service-and-price-store';

@Component({
  tag: 'pn-find-service-and-price',
  styleUrl: 'pn-find-service-and-price.scss',
})
export class PnfindServiceAndPrice {
  @Prop() source: string = 'https://com-inte.postnord.com/globalassets/global/prices/sv-se-findprice.xlsx';
  @Prop({ mutable: true }) language: string = 'sv';
  @Prop({ mutable: true }) market: string = 'se';

  @State() filteredItems: ProductPriceItem[] = [];
  @State() postagetype: FindServiceAndPricePostageType = {
    value: '',
    name: '',
    icon: '',
  } as FindServiceAndPricePostageType;
  @State() weight: FindServiceAndPriceWeight = { value: 0, name: '' } as FindServiceAndPriceWeight;
  @State() weightvalue: number = 0;
  @State() deliveryscope: FindServiceAndPriceDeliveryScope = {
    value: '',
    name: '',
    icon: '',
  } as FindServiceAndPriceDeliveryScope;
  @State() sourceData: ProductPriceSourceData = { types: [], data: [] };

  componentWillLoad() {
    this.setState();
    this.getDataSource();

    // window.setTimeout(() => {
    //   this.postagetype = postagetypes[0];
    //   this.deliveryscope = deliveryscopes[this.postagetype.value][0];
    //   this.weight = weights[this.postagetype.value][0];
    //   this.weightvalue = this.weight.value;
    // }, 500)
  }

  async getDataSource() {
    const cacheBusting = `?cache=${Math.floor(Math.random() * (10000 - 1) + 1)}`;
    const req = await fetch(this.source + cacheBusting);
    const reqbody = await req.blob();
    try {
      const convertedData = await convertFileFromStream(reqbody);
      this.sourceData = convertedData;
      this.filterItems();
    } catch (e) {
      console.log('Unable to parse data');
    }
  }

  @Watch('market')
  @Watch('language')
  setState() {
    if (this.market) {
      state.market = markets[this.market] as PnfindServiceAndPriceMarket;
    }
    if (this.language && translations[this.language]) {
      state.i18n = translations[this.language] as PnfindServiceAndPricei18n;
    }
  }

  @Watch('postagetype')
  @Watch('deliveryscope')
  @Watch('weightvalue')
  filterItems() {
    // this.debug = `Postagetype: ${this.postagetype.value}. <br/>Weight: ${ this.weight.value }`;
    if (!this.sourceData?.data) {
      console.log('Data has not yet been loaded');
      return;
    }
    if (!this.postagetype) {
      return;
    }
    const typeData = this.sourceData.data.filter(x => x.what === this.postagetype.value);
    if (!this.deliveryscope) {
      return;
    }
    const deliveryScopeData = typeData.filter(x => x.deliveryscope === this.deliveryscope.value);
    if (!this.weight || !this.weight.value) {
      return;
    }

    // For letters we use grams. Otherwise we use KG
    const comparisonWeight = this.weight.abbreviation.toLocaleLowerCase() === 'kg' && this.postagetype.value === 'letter' ? this.weight.value * 1000 : this.weight.value;

    let weightFilteredData = deliveryScopeData.filter(x => x.maxweight === comparisonWeight);

    // In parcels the maxwidths don't necessarily start at 1kg so we fetch any type below the user selected value
    if (weightFilteredData.length === 0) {
      weightFilteredData = deliveryScopeData.filter(x => x.maxweight >= comparisonWeight);
      if (weightFilteredData.length > 0) {
        // We reverse the result array since the map will take the last unique item instead of the first
        const uniqueWeightOptions = [...new Map(weightFilteredData.reverse().map(item => [item['id'], item])).values()];
        if (uniqueWeightOptions.length > 0) {
          weightFilteredData = uniqueWeightOptions;
        }
      }
    }

    this.filteredItems = weightFilteredData;
  }

  render() {
    return (
      <div>
        <div class="findprice-form">
          {state.i18n.componentheading ? <h2>{state.i18n.componentheading}</h2> : null}
          <div class="postagetypes">
            {postagetypes.map(postagetype => (
              <pn-choice-button
                choiceid={postagetype.value}
                value={postagetype.value}
                name="postagetype"
                type="radio"
                checked={postagetype.value === this.postagetype.value}
                onChange={() => {
                  this.postagetype = (postagetypes as FindServiceAndPricePostageType[]).filter(x => x.value === postagetype.value)[0];
                  this.deliveryscope = { value: '', name: '', icon: '' } as FindServiceAndPriceDeliveryScope;
                  this.weight = { value: 0, name: '' } as FindServiceAndPriceWeight;
                  this.weightvalue = this.weight.value;
                }}
              >
                <pn-illustration illustration={postagetype.illustration}></pn-illustration> {postagetype.name}
              </pn-choice-button>
            ))}
          </div>

          {this.postagetype.value && deliveryscopes[this.postagetype.value] ? (
            <div>
              {state.i18n.deliveryscopeheading ? <div class="heading">{state.i18n.deliveryscopeheading}</div> : null}
              <div class="deliveryscope">
                {deliveryscopes[this.postagetype.value].map(deliveryscope => (
                  <pn-choice-button
                    choiceid={this.postagetype.value + deliveryscope.value}
                    value={deliveryscope.value}
                    name="deliveryscope"
                    type="radio"
                    checked={deliveryscope.value === this.deliveryscope.value}
                    onChange={() => {
                      this.deliveryscope = (deliveryscopes[this.postagetype.value] as FindServiceAndPriceDeliveryScope[]).filter(x => x.value === deliveryscope.value)[0];
                      if (this.weight.value === 0) {
                        this.weight = weights[this.postagetype.value][0];
                        this.weightvalue = this.weight.value;
                      }
                    }}
                  >
                    <pn-illustration illustration={deliveryscope.illustration}></pn-illustration> {deliveryscope.name}
                  </pn-choice-button>
                ))}
              </div>
            </div>
          ) : null}

          {this.postagetype.value && weights[this.postagetype.value] && this.deliveryscope.value ? (
            <div>
              {state.i18n.sizeheading ? <div class="heading">{state.i18n.sizeheading}</div> : null}
              <div class="weightoption">
                <div class="weightoption-input">
                  <pn-input
                    inputid="weightvalue"
                    name="weightvalue"
                    type="number"
                    label={state.i18n.weightlabel}
                    value={this.weightvalue}
                    onChange={e => {
                      if (e && e.target && e.target.value) {
                        const weightNum = parseInt(e.target.value, 10);
                        if (!isNaN(weightNum)) {
                          let matchingWeights = (weights[this.postagetype.value] as FindServiceAndPriceWeight[]).filter(x => {
                            let weightInGrams = x.abbreviation === 'kg' ? x.value * 1000 : x.value;
                            return weightInGrams >= weightNum;
                          });
                          if (matchingWeights && matchingWeights.length > 0) {
                            this.weight = matchingWeights[0];
                            this.weightvalue = this.weight.value;
                          }
                        }
                      }
                    }}
                  ></pn-input>
                  <span class="weightoption-input-abbreviation">{this.weight.abbreviation}</span>
                </div>
                <div class="weightoption-range">
                  <input
                    type="range"
                    id="weightrange"
                    name="weightrange"
                    list="weightlist"
                    value={!this.weight.value ? 0 : weights[this.postagetype.value].findIndex(x => x.value === this.weightvalue)}
                    onChange={e => {
                      const value = (e.target as HTMLInputElement).value;
                      this.weight = weights[this.postagetype.value][value];
                      this.weightvalue = this.weight.value;
                    }}
                    min="0"
                    // max={ weights[this.postagetype.value][weights[this.postagetype.value].length - 1].value }
                    max={weights[this.postagetype.value].length - 1}
                  />
                  <datalist id="weightlist">
                    {weights[this.postagetype.value].map((weight, weightindex) => {
                      let labelText = weight.name;
                      if (weights[this.postagetype.value].length > 6) {
                        labelText = weightindex % 5 === 0 || weightindex === 0 || weightindex === weights[this.postagetype.value].length - 1 ? weight.name : '';
                      }
                      return <option value={weightindex} {...(labelText ? { label: labelText } : {})}></option>;
                    })}
                  </datalist>
                </div>
              </div>
            </div>
          ) : null}
        </div>
        {this.filteredItems && this.filteredItems.length > 0 ? (
          <div class="resultlist">
            {state.i18n.resultlistheading ? <div class="heading">{state.i18n.resultlistheading}</div> : null}
            {this.filteredItems.map((item: ProductPriceItem) => (
              <div>
                <pn-find-service-and-price-result item={item}></pn-find-service-and-price-result>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }
}
